import {ChangeDetectorRef, Component, EventEmitter, Inject, Injector, OnDestroy, OnInit, Output} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {catchError, filter, switchMap, take, takeUntil} from 'rxjs/operators';
import {MxCustomizableCustomizer} from '../../../../mx-customizer';
import {CopyPasteConfig} from '../../../../shared/interfaces/copy-paste-config';
import {MxCopyPasteService} from '../../../../shared/services/mx-copy-paste.service';
import {MxNotificationService} from '../../../../shared/services/mx-notification.service';
import {MX_PERMISSION_TOKEN, MxPermissionService} from '../../../../shared/services/mx-permission.service';
import {MxPreviousRouteService} from '../../../../shared/services/mx-previous-route.service';
import {
    MX_TITLE_SERVICE_TOKEN,
    MxTitleAndBreadcrumbsService
} from '../../../../shared/services/mx-title-and-breadcrumbs.service';
import {MxUrlUtilsService} from '../../../../shared/services/mx-url-utils.service';
import {MxUtilsService} from '../../../../shared/services/mx-utils.service';
import {MxButtonModel} from '../../../mx-button/components/mx-button.model';
import {MxConfirmDialogComponent} from '../../../mx-dialogs/components/mx-confirm-dialog/mx-confirm-dialog.component';
import {MxFormDeactivation} from '../../guards/can-deactivate-form.guard';
import {MxFieldCustomizerService} from '../../modules/mx-dynamic-fields/services/mx-field-customizer.service';
import {MxCloseConfirmationService} from '../../services/mx-close-confirmation.service';
import {MxFormErrorMessageService} from '../../services/mx-form-error-message.service';
import {
    FormActionsConfig,
    FormPropsConfig,
    MxFormConfig,
    MxFormCustomizerEvent,
    MxFormMode,
    ObjectMetaData,
    OnBoFormBeforeSubmit,
    OnBoFormMetadataReady,
    OnBoFormReady,
    PageActions
} from '../../services/mx-form-fields-builder.service';
import {MxObjectFormValidatorService} from '../../services/mx-object-form-validator.service';
import {MxObjectService} from '../../services/mx-object.service';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../shared/services/mx-translate.service';

@Component({
    selector: 'mx-object-form',
    standalone: false,
    templateUrl: './mx-object-form.component.html',
    styleUrls: ['./mx-object-form.component.scss'],
    providers: [ MxFieldCustomizerService ]
})
export class MxObjectFormComponent implements OnInit, MxFormDeactivation, OnBoFormMetadataReady, OnBoFormReady, OnDestroy,
    MxCustomizableCustomizer<MxFormCustomizerEvent> {

    @Output() customize: EventEmitter<MxFormCustomizerEvent> = new EventEmitter<MxFormCustomizerEvent>();
    @Output() formClose: EventEmitter<{submit: boolean, back: boolean, response: boolean }> =
        new EventEmitter<{submit: boolean, back: boolean, response: boolean }>();

    copyButton: MxButtonModel;
    pasteButton: MxButtonModel;
    cancelButton: MxButtonModel;
    submitButton: MxButtonModel;
    backButton: MxButtonModel;

    form: FormGroup;
    public formMode$$: BehaviorSubject<MxFormMode> = new BehaviorSubject<MxFormMode>('edit');

    public copyPasteConfig: CopyPasteConfig;
    public copyPasteFunctionality: boolean = false;
    public pageActions: Array<PageActions>;

    public customActions: Array<MxButtonModel> = [];

    private boFormConfig: MxFormConfig;
    private readonly dialogRef: MatDialogRef<MxObjectFormComponent>;
    private responseForDialog: any;
    private readonly navigateBackFn: Function;
    private titleTemplate: string;
    private initiallyEnabledControls = new Set<string>();

    private updateForm$$: BehaviorSubject<{id?: string, copy?: boolean}> = new BehaviorSubject({});
    private _destroy$$: Subject<null> = new Subject<null>();
    private disablePaste: boolean = true;
    private beforeSubmitCustomizers: Array<OnBoFormBeforeSubmit> = [];
    public isSubmitDisabled = (): boolean => !!(this.form == null || this.form.pristine || this.form.pending || this.form.invalid);

    private defaultFormActions: FormActionsConfig =
        {
            submit: {
                text: 'OK',
                visible: true,
                disableFunction: this.isSubmitDisabled
            },
            cancel: {
                text: 'Cancel',
                visible: true
            }
        };

    private defaultProps: FormPropsConfig = {
        showNavigation: true,
        navigateBackOnSave: true,
        navigateBackOnCancel: true,
        addPrefixToTitle: true,
        submitFunction: (form: FormGroup, formMode: MxFormMode, url: string): void => {
            this.save(form, formMode, url);
        },
        notifyOnSaveFn: (saveOpWithPipe$: Observable<any>, action:  'update' | 'create'): Observable<any> => {
            let redirectionFn = (response: any): void => {
                this.router.navigate([this.urlService.constructUrl(this.boFormConfig.routeLink, this.route), response]);
            };
            return this.notificationService.notifyByObject(saveOpWithPipe$,
                action, this.form.getRawValue(),
                this.boFormConfig.redirectionMsg === false ? null : redirectionFn,
                this.boFormConfig.notificationMsg)
                .pipe(
                    take(1),
                    filter(value => value === null || !value.error)
                );
        }
    };

    public props: FormPropsConfig = this.defaultProps;
    public actionsConfig: FormActionsConfig = this.defaultFormActions;

    constructor(
                private closeConfirmationServiceService: MxCloseConfirmationService,
                private boObjectService: MxObjectService,
                public route: ActivatedRoute,
                private asyncValidator: MxObjectFormValidatorService,
                private previousRouteService: MxPreviousRouteService,
                private router: Router,
                private dialog: MatDialog,
                @Inject(MX_TITLE_SERVICE_TOKEN) private titleService: MxTitleAndBreadcrumbsService,
                @Inject(MX_PERMISSION_TOKEN) public permissionService: MxPermissionService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) public translateService: MxTranslateService,
                private urlService: MxUrlUtilsService,
                private notificationService: MxNotificationService,
                private errorService: MxFormErrorMessageService,
                private injector: Injector,
                public copyPasteService: MxCopyPasteService,
                private cdRef: ChangeDetectorRef,
                private utils: MxUtilsService) {
            let defaultValue = {...this.route.snapshot.data, fromRoute: true};
            this.boFormConfig = injector.get(MAT_DIALOG_DATA, defaultValue) || defaultValue;
            let formModeFromParams: MxFormMode = this.route.snapshot.queryParams.formMode
                                                || (this.route.snapshot.params.id === 'new' ? 'new' : 'edit');
            let editPermission = this.boFormConfig.samePermissionForEdit === true || !this.boFormConfig.permission
                ? this.boFormConfig.permission
                : this.permissionService.getEditPermission(this.boFormConfig.permission);
            if (this.boFormConfig.fromRoute) {
                this.boFormConfig.id = this.route.snapshot.params.id || this.boFormConfig.id;
                if (this.boFormConfig.id === 'new') {
                    this.formMode$$.next(formModeFromParams === 'duplicate' ? 'duplicate' : 'new');
                    this.boFormConfig.id = null;
                } else if (this.boFormConfig.readonly
                    || (this.boFormConfig.permission
                        && !this.permissionService.hasPermission(editPermission))) {
                    this.formMode$$.next('readonly');
                } else {
                    this.formMode$$.next(formModeFromParams);
                }
            } else {
                this.dialogRef = this.injector.get(MatDialogRef);
                this.defaultFormActions.submit.visible = false;
                this.defaultFormActions.cancel.text = 'Close';
                this.defaultProps.addPrefixToTitle = false;
                if (this.boFormConfig.readonly
                    || (this.boFormConfig.permission
                        && !this.permissionService.hasPermission(editPermission))) {
                    this.formMode$$.next('readonly');
                }
                if (this.boFormConfig.id === 'new') {
                    this.formMode$$.next('new');
                }
            }
            this.alignDefaultActionToMode();
            this.navigateBackFn = this.previousRouteService.navigateToParentFunc(this.route, this.getNavigateBackToRouteParent());
    }

    ngOnInit(): void {
        this.formMode$$.pipe(
            takeUntil(this._destroy$$))
            .subscribe(() => {
                this.doReload();
            });

        this.updateButtons();
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    boOnMetadataReady(metaData: ObjectMetaData): ObjectMetaData {
        if (this.boFormConfig.hasAsyncValidation === undefined && metaData && metaData.asyncValidation !== undefined) {
            this.boFormConfig.hasAsyncValidation = metaData.asyncValidation;
        }
        if (metaData && metaData.describeOfObj !== undefined) {
            this.titleTemplate = metaData.describeOfObj;
        }
        if (!this.boFormConfig.notificationMsg && metaData && metaData.describeOfObj !== undefined) {
            this.boFormConfig.notificationMsg = (object: any) => this.getObjectDescription(metaData.describeOfObj, object);
        }
        return metaData;
    }

    boOnFormReady(form: FormGroup): void {
        if (this.formMode$$.value === 'duplicate'
            && this.copyPasteService.canPaste(this.getCopyPasteConf().classType)) {
            this.notificationService.showSuccessByObject(form.getRawValue(), 'paste', this.boFormConfig.notificationMsg);
        }
        if (this.boFormConfig.hasAsyncValidation
            && this.formMode$$.value !== 'readonly'
            && form && !form.asyncValidator
        ) {
            form.setAsyncValidators(this.errorService.asyncValidator(this.asyncValidator,
                this.urlService.constructUrl(this.boFormConfig.restLink, this.route)));
        }
        if (this.route.snapshot.data['titleTemplate'] || (this.boFormConfig.fromRoute && this.props.showNavigation)) {
            let titleField = this.route.snapshot.data['titleTemplate'] || this.titleTemplate;
            let title;
            if (titleField) {
                title = this.getObjectDescription(titleField, form.getRawValue());
            } else {
                title = form.get('name');
            }
            if (this.titleService) {
                this.titleService.setTitle(title);
            }
        }
        if (this.copyPasteConfig && this.copyPasteConfig.disablePaste) {
            this.disablePaste =  this.copyPasteConfig.disablePaste(form.value);
        } else {
            this.disablePaste = false;
        }
        this.form = form;
        this.formClose
            .pipe(
                takeUntil(this._destroy$$)
            )
            .subscribe((closeParam) => {
                if (closeParam.submit) {
                    this.form.markAsPristine();
                }
                if (this.dialogRef) {
                    this.dialogRef.close();
                    if (this.props.returnResponseAfterSaveInDialog) {
                        this.dialogRef.close( closeParam.response ? this.responseForDialog : null);
                    } else {
                        this.dialogRef.close( closeParam.response ? this.form.value : null);
                    }
                } else if (closeParam.back) {
                    this.navigateBackFn();
                }
            });
    }

    onCustomize(boFormCustomizerEvent: MxFormCustomizerEvent): void {
        boFormCustomizerEvent.customizeBoFormConfig(this.boFormConfig);
        boFormCustomizerEvent.customizeProps(this.defaultProps);
        boFormCustomizerEvent.customizeFormMode(this.formMode$$.value);
        boFormCustomizerEvent.customizeCloseConfirmationFunction(this.closeConfirmation);
        this.customize.emit(boFormCustomizerEvent);
        if (this.boFormConfig.onCustomize) {
            this.boFormConfig.onCustomize(boFormCustomizerEvent);
        }
        this.pageActions = boFormCustomizerEvent.pageActions;
        this.customActions = boFormCustomizerEvent.customActions;
        this.boFormConfig = boFormCustomizerEvent.getBoFormConfig();
        this.copyPasteConfig = boFormCustomizerEvent.getCopyPasteConfig();
        this.copyPasteFunctionality = !!boFormCustomizerEvent.getCopyPasteConfig();
        if (!boFormCustomizerEvent.getCopyPasteConfig() || !this.copyPasteConfig.classType) {
            if (!this.copyPasteConfig) {
                this.copyPasteConfig = {classType: this.boFormConfig.className};
            } else {
                this.copyPasteConfig.classType = this.boFormConfig.className;
            }
        }
        if (!this.props.informationKey) {
            this.props.informationKey = boFormCustomizerEvent.getBoFormConfig().formName;
        }
        this.props = boFormCustomizerEvent.getProps();
        if (boFormCustomizerEvent.formMode.value) {
            this.formMode$$ = boFormCustomizerEvent.formMode;
        }
        this.alignDefaultActionToMode();

        if (this.formMode$$ && this.formMode$$.value === 'duplicate') {
            let idFromCache = this.copyPasteService.getData(this.copyPasteConfig.classType);
                if (idFromCache) {
                    this.boFormConfig.id = '' + idFromCache;
                }
                if (!this.copyPasteConfig.convertFn) {
                    this.copyPasteConfig.convertFn = this.copyPasteService.getPasteFn(this.copyPasteConfig.classType);
                }
        }
        this.beforeSubmitCustomizers = boFormCustomizerEvent.boFormBeforeSubmitCustomizers;
        this.actionsConfig = boFormCustomizerEvent.getActionsConfig(this.defaultFormActions);
        this.updateButtons();
        if (this.formMode$$.value === 'readonly') {
            this.actionsConfig.submit.visible = false;
        }
        if (!boFormCustomizerEvent.fieldsDataConfig.className
            && !boFormCustomizerEvent.fieldsDataConfig.metaObject
            && !boFormCustomizerEvent.fieldsDataConfig.metaObservable) {
            boFormCustomizerEvent.fieldsDataConfig.className = this.boFormConfig.className;
            boFormCustomizerEvent.fieldsDataConfig.force = this.boFormConfig.force;
        }
        if (!boFormCustomizerEvent.fieldsDataConfig.valueObject
            && !boFormCustomizerEvent.fieldsDataConfig.valueObservable) {
            boFormCustomizerEvent.fieldsDataConfig.valueObservable = this.getInitialDataSource();
        }
        boFormCustomizerEvent.addCustomizer(this);
        this.cdRef.detectChanges();
    }

    getInitialDataSource(): Observable<any> {
        return this.updateForm$$
            .pipe(
                switchMap((params: {id?: string, copy?: boolean}) => {
                    let objectId = params.id ? params.id : this.boFormConfig.id;

                    if (this.formMode$$.value === 'new' && !params.copy) {
                        return of({});
                    }

                    if (objectId) {
                        let url = this.urlService.constructUrl(this.boFormConfig.restLink,
                            this.route) + (this.boFormConfig.addIdToUrl !== false ? ('/' + objectId) : '');
                        return this.boObjectService.getObject(url)
                            .pipe(
                                switchMap(value => {
                                    let copyPasteConfig = this.getCopyPasteConf();
                                    if (this.formMode$$.value !== 'edit'
                                        && copyPasteConfig.convertFn
                                        && this.copyPasteService.canPaste(copyPasteConfig.classType)) {
                                        return of(copyPasteConfig.convertFn(value));
                                    } else {
                                        return of(value);
                                    }
                                })
                            );
                    } else {
                        console.error(`Form ${this.boFormConfig.formName} didn't supply valid id`);
                        return of({});
                    }
                })
            );
    }

    public showHeader(): boolean {
        return this.props.addPrefixToTitle
            || this.props.showNavigation
            || !!this.route.snapshot.data.formName
            || !!this.pageActions
            || !!this.boFormConfig.title;
    }

    public isMultipleColumns(): boolean {
        return this.boFormConfig.multipleColumns;
    }

    public doReload(params?: {id?: string, copy?: boolean}): void {
        this.updateForm$$.next(params ? params : {});
    }

    public submitForm(): void {
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.setFormReadonly(true);
        if (this.props.closeOnSubmit) {
            this.formClose.emit({submit: true, back: false, response: true});
            return;
        }
        this.props.submitFunction(this.form, this.formMode$$.value, this.urlService.constructUrl(this.boFormConfig.restLink, this.route),
            () => this.setFormReadonly(false));
    }

    public isFormReadyToSubmit(): boolean {
        return this.form.valid;
    }

    public showErrorMessagesForInvalidFields(): void {
        this.form.markAllAsTouched();
        Object.keys(this.form.controls).forEach(key => {
            this.form.get([key]).markAsDirty();
        });
    }

    public setFormMode(formMode: MxFormMode): void {
        // This will trigger doReload
        this.formMode$$.next(formMode);
    }

    getFormTitle(): string {
        let title: string = this.boFormConfig.title || this.route.snapshot.data.formName || '';
        if (this.props.addPrefixToTitle) {
            if (this.formMode$$.value === 'new' || this.formMode$$.value === 'duplicate') {
                title = 'Add ' + title;
            } else if (this.formMode$$.value === 'edit') {
                title = 'Edit ' + title;
            }
        }

        return this.translateService.getTranslatedLabel(title);
    }

    closeConfirmation: () => Observable<boolean> = () => {
        return this.closeConfirmationServiceService.closeConfirmation(this.form && this.form.dirty);
    };

    onCancel = () => {
        if (this.props.customNavigation?.onCancel) {
            this.props.customNavigation.onCancel();
            return;
        }

        this.formClose.emit({submit: false, back: this.props.navigateBackOnCancel !== false, response: false});
    };

    onCopy = () => {
        this.copyPasteService.setData(this.copyPasteConfig.classType, this.form.getRawValue());
        this.notificationService.showSuccessByObject(this.form.getRawValue(), 'copie', this.boFormConfig.notificationMsg);
    };

    onPaste = () => {
        let item = this.copyPasteService.getData(this.copyPasteConfig.classType, this.copyPasteConfig.convertFn);
        let rawItem = this.copyPasteService.getData(this.copyPasteConfig.classType);
        let pasteFn = () => {
            if (this.copyPasteService.isFullObject(this.copyPasteConfig.classType)) {
                this.form.patchValue(item as object);
                this.form.markAsDirty();
            } else if (item) {
                this.doReload({id: '' + <number>item, copy: true});
            }
            this.notificationService.showSuccessByObject(rawItem, 'paste', this.boFormConfig.notificationMsg);
        };
        if (this.copyPasteConfig.askOnPasteDialog &&
                this.copyPasteConfig.askOnPasteDialog(this.form)) {
            this.dialog.open(MxConfirmDialogComponent,
            {
                panelClass: 'mx-dialog-panel-class',
                data: {
                    title: `Paste ${this.boFormConfig.formName} Warning`,
                        message: 'Current item already has a filter. Would you like to replace existing filter?',
                        type: 'warn'
                }
            }
            ).afterClosed().subscribe((result: boolean) => {
                if (result) {
                    pasteFn();
                }
            });
        } else {
            pasteFn();
        }
    };

    hasPastePermission(): boolean {
        if (!this.copyPasteConfig || this.disablePaste) {
            return false;
        }
        let canPaste = true;
        let permWithoutAction = this.permissionService.getPermissionWithoutAction(this.boFormConfig.permission);
        if (!this.permissionService.hasRole(permWithoutAction)
            && this.formMode$$.value === 'edit' && !this.permissionService.hasPermission(permWithoutAction + '.edit')) {
            canPaste = false;
        }
        if (!this.permissionService.hasRole(permWithoutAction)
            && (this.formMode$$.value === 'new' || this.formMode$$.value === 'duplicate')
            && !this.permissionService.hasPermission(permWithoutAction + '.add')) {
            canPaste = false;
        }
        return canPaste && this.copyPasteService.canPaste(this.copyPasteConfig.classType);
    }

    hasCopyPermission(): boolean {
        if (!this.copyPasteConfig) {
            return false;
        }
        let permWithoutAction = this.permissionService.getPermissionWithoutAction(this.boFormConfig.permission);
        return this.permissionService.hasRole(permWithoutAction)
            || this.permissionService.hasPermission(`(${permWithoutAction}.add || ${permWithoutAction}.edit)`);
    }

    hasActionPermission(pageAction: PageActions): boolean {
        return !pageAction.permission || this.permissionService.hasPermission(pageAction.permission);
    }

    private onSubmit = () => {
        if (this.isFormReadyToSubmit()) {
            this.submitForm();
        } else {
            this.showErrorMessagesForInvalidFields();
            if (this.props.showErrorOnSubmitInDialog) {
                this.notificationService.showFormValidationMessage(this.form);
            }
        }
    };

    private getObjectDescription = (template: string, obj: any): string => {
        return template.replace(/#(\w+)/g, (_, value) => {
            let field = obj[value];
            return (field) ? field.value ? field.value : field : '';
        });
    };

    private alignDefaultActionToMode(): void {
        this.defaultFormActions.submit.text = this.formMode$$.value === 'edit' ? 'Save' :
            this.formMode$$.value === 'duplicate' ? 'Duplicate' :
                this.formMode$$.value === 'new' ? 'Add' : 'OK';
    }

    private updateButtons(): void {
        this.copyButton = {
            label: 'Copy',
            tooltip: 'Copy',
            className: 'mxs-button--secondary',
            clickHandler: this.onCopy
        };

        this.pasteButton = {
            label: 'Paste',
            tooltip: 'Paste',
            className: 'mxs-button--secondary',
            clickHandler: this.onPaste
        };

        this.cancelButton = {
            label: this.actionsConfig!.cancel!.text,
            tooltip: this.actionsConfig!.cancel!.text,
            className: 'mxs-button--secondary',
            clickHandler: this.onCancel
        };

        this.submitButton = {
            label: this.actionsConfig.submit.text,
            tooltip: this.actionsConfig!.submit!.text,
            disabled: this.actionsConfig!.submit!.disableFunction,
            className: 'mxs-button--primary',
            clickHandler: this.onSubmit
        };

        this.backButton = {
            icon: 'west',
            name: 'back',
            className: 'mxs-button--navigation',
            clickHandler: this.onCancel

        };
    }

    private save(form: FormGroup, formMode: MxFormMode, url: string): void {
        this.beforeSubmitCustomizers.forEach(c => c.boOnBeforeSubmit(form));
        let saveOp$: Observable<any>;
        let addId: boolean = this.boFormConfig.addIdToUrl !== false;
        let id = this.boFormConfig.id ? this.boFormConfig.id : this.route.snapshot.params.id;
        let rawValue = form.getRawValue();
        rawValue = this.utils.convertPayloadDatesToTimestamps(rawValue);
        if (formMode === 'edit' && (!!id || !addId)) {
            saveOp$ = this.boObjectService.saveEditObject(rawValue,
                url + (addId ? '/' + (id) : ''));
        } else {
            saveOp$ = this.boObjectService.saveNewObject(rawValue, url);
        }

        let saveOpWithPipe$ = saveOp$.pipe(
            catchError(
                (error: any) => {
                    if (error.status === 400 && error.error && error.error.fieldErrors) {
                        this.errorService.setServerErrors(error.error, this.form);
                        return of({error: error});
                    }
                    this.setFormReadonly(false);
                    throw error;
                }
            ));
        this.props.notifyOnSaveFn(saveOpWithPipe$, formMode === 'edit' ? 'update' : 'create')
            .subscribe(response => {
                if (this.props.customNavigation?.onSubmitRedirect) {
                    this.props.customNavigation.onSubmitRedirect(response);
                    return;
                }

                if (this.props.returnResponseAfterSaveInDialog) {
                    this.responseForDialog = response;
                }
                form.markAsUntouched();
                form.markAsPristine();
                this.setFormReadonly(false);
                this.formClose.emit({
                    submit: true,
                    back: this.props.navigateBackOnSave === true,
                    response: this.props.returnResponseAfterSaveInDialog === true
                });
            });
    }

    private getNavigateBackToRouteParent(): boolean {
        return this.boFormConfig.navigateBackToRouteParent;
    }

    private getCopyPasteConf(): CopyPasteConfig {
        return this.copyPasteConfig;
    }

    private setFormReadonly(readonly: boolean) {
        Object.entries(this.form.controls).forEach(([name, control]) => {
            if (readonly) {
                if (control.enabled) {
                    control.disable({emitEvent: false});
                    this.initiallyEnabledControls.add(name);
                }
            } else {
                if (this.initiallyEnabledControls.has(name)) {
                    control.enable({emitEvent: false});
                    this.initiallyEnabledControls.delete(name);
                }
            }
        });
    }
}
